import {
  QueryClient,
  QueryFunctionContext,
  UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { GetServerSidePropsContext } from "next";

import httpClient from "helpers/hybridHttpClient";

import type {
  BlockBlockableParams,
  BlockableResponse,
  NoAccessBlockableResponse,
} from "types/courses/block";
import { BLOCK_QUERY_KEY_PREFIX } from "./constants";

interface BlockableQueryKeyFactoryOptions {
  params: BlockBlockableParams;
}

export const generateBlockableByBlockPivotQueryKey = (
  options: BlockableQueryKeyFactoryOptions,
) => [BLOCK_QUERY_KEY_PREFIX, "blockable", options] as const;

type BlockableByBlockPivotQueryKey = ReturnType<
  typeof generateBlockableByBlockPivotQueryKey
>;

const fetchBlockableByBlockPivot = async (
  params: BlockBlockableParams,
  ctx?: GetServerSidePropsContext,
) => {
  try {
    const { data } = await httpClient(ctx).get<BlockableResponse>(
      "/api/courses-user-new/block/blockable/",
      {
        params,
      },
    );

    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;
      // If the response code is 403, it means the user doesn't have access to the block
      // and we should return a special type of response to handle this case in the UI
      if (statusCode === 403) {
        return {
          type: "no_access",
          blockable: null,
        } as NoAccessBlockableResponse;
      }
    }

    throw error;
  }
};

const blockableByBlockPivotQueryFnFactory =
  (ctx?: GetServerSidePropsContext) =>
  ({
    queryKey: [, , { params }],
  }: QueryFunctionContext<BlockableByBlockPivotQueryKey>) =>
    fetchBlockableByBlockPivot(params, ctx);

type useBlockableByBlockPivotQueryFetchOptions<TData> =
  BlockableQueryKeyFactoryOptions & {
    ctx?: GetServerSidePropsContext;
  } & Omit<
      UseQueryOptions<
        BlockableResponse,
        AxiosError,
        TData,
        BlockableByBlockPivotQueryKey
      >,
      "queryKey"
    > & {
      onSuccess?: (data: BlockableResponse) => void;
    };

export const useBlockableByBlockPivot = <TData = BlockableResponse>({
  ctx,
  params,
  ...options
}: useBlockableByBlockPivotQueryFetchOptions<TData>) => {
  return useQuery({
    ...options,
    queryKey: generateBlockableByBlockPivotQueryKey({
      params,
    }),
    queryFn: blockableByBlockPivotQueryFnFactory(ctx),
  });
};

useBlockableByBlockPivot.generateKey = generateBlockableByBlockPivotQueryKey;

useBlockableByBlockPivot.fetchQuery = <TData = BlockableResponse>(
  queryClient: QueryClient,
  { ctx, params, ...options }: useBlockableByBlockPivotQueryFetchOptions<TData>,
) =>
  queryClient.fetchQuery({
    ...options,
    queryKey: generateBlockableByBlockPivotQueryKey({
      params,
    }),
    queryFn: blockableByBlockPivotQueryFnFactory(ctx),
  });

useBlockableByBlockPivot.prefetchQuery = <TData = BlockableResponse>(
  queryClient: QueryClient,
  { ctx, params, ...options }: useBlockableByBlockPivotQueryFetchOptions<TData>,
) =>
  queryClient.prefetchQuery({
    ...options,
    queryKey: generateBlockableByBlockPivotQueryKey({
      params,
    }),
    queryFn: blockableByBlockPivotQueryFnFactory(ctx),
  });
